<template>
  <div class="table-container"
       :class="{ 'table-sm': size === 'sm', 'table-xs': size === 'xs' }"
       :style="{ maxHeight }">
    <table class="table custom-scrollbar"
           :class="{ 'table-sm': size === 'sm', 'table-xs': size === 'xs', striped: striped }">
      <colgroup>
        <slot name="colgroup"></slot>
      </colgroup>
      <thead>
      <slot name="thead-top"></slot>
      <tr class="text-center">
        <th v-if="selectable"></th>
        <th v-for="field in fieldsToDisplay"
            :key="field.label"
            class="table-header"
            @click="switchSort(field.name)"
            :style="{ cursor: field.sortable ? 'pointer' : 'default' }">
          {{ field.label }}
          <b-iconstack v-if="field.sortable && !field.sortOrder"
                       font-scale="0.4">
            <b-icon-triangle-fill stacked
                                  shift-h="4"
                                  shift-v="16"/>
            <b-icon-triangle-fill flip-v
                                  stacked
                                  shift-h="4"
                                  shift-v="0"/>
          </b-iconstack>
          <b-icon-triangle-fill v-if="field.sortOrder === 'ASC'"
                                shift-h="4"
                                shift-v="4"
                                font-scale="0.5"/>
          <b-icon-triangle-fill flip-v
                                shift-h="4"
                                shift-v="4"
                                v-else-if="field.sortOrder === 'DESC'"
                                font-scale="0.5"/>
        </th>
      </tr>
      </thead>

      <tbody v-if="items.length > 0">
      <tr v-for="(item, row) in items"
          :key="row"
          :style="{ cursor: clickable ? 'pointer' : 'default'}"
          @click="onRowClick(item, row)">

        <!-- Check box -->
        <td v-if="selectable"
            class="text-center"
            style="cursor: pointer"
            @click.stop="onSelect(item, items, row)">
          <span v-if="selected(item)">
            <checked-box/>
          </span>
          <span v-else>
            <empty-box/>
          </span>
        </td>
        <!-- /Check box -->

        <slot v-for="(field, col) in fieldsToDisplay"
              :name="`cell(${field.name})`"
              :item="item"
              :items="items"
              :value="value(field, item)"
              :field="field"
              :row="row"
              :col="col">
          <td :key="col"
              class="text-center">
            {{ formatted(field, item) }}
          </td>
        </slot>
      </tr>
      <slot name="footer"></slot>
      </tbody>

      <tbody v-else>
      <tr>
        <td :colspan="fields.length + Number(selectable)"
            class="text-center">
          조회 결과가 없습니다
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import EmptyBox from "@/components/esg/icons/EmptyBox.vue";
import CheckedBox from "@/components/esg/icons/CheckedBox.vue";

export default {
  name: "EsgBaseTable",
  components: { CheckedBox, EmptyBox },
  computed: {
    fieldsToDisplay() {
      return this.fields.filter(field => field.list)
    },
  },
  props: {
    clickable: {
      type: Boolean,
      default: true
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selectedItems: {
      type: Array,
      required: false
    },
    striped: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'md'
    },
    maxHeight: {
      type: String,
      default: '270px'
    },
    items: {
      type: Array,
      default() {
        return [
          // Example
          { id: 1, name: 'example', age: 23, created: '2023-01-01 23:00:00' },
        ];
      }
    },
    fields: {
      type: Array,
      default() {
        return [
          // Example
          { name: 'id', label: 'ID', sortable: true, sort: null },
          { name: 'name', label: '이름', sortable: false, sort: null },
          { name: 'age', label: '나이', sortable: false, sort: null },
          { name: 'created', label: '생성일', sortable: false, sort: null }
        ];
      },
    },
  },
  methods: {
    value(field, item) {
      const key = field.name.replace('.', '_')
      const camelCaseKey = this.snakeToCamel(key);
      const alias = field.alias;
      if (item[key] !== undefined && item[key] !== null) {
        return item[key];
      } else if (item[camelCaseKey] !== undefined && item[camelCaseKey] !== null) {
        return item[camelCaseKey];
      } else {
        return item[alias];
      }
    },
    formatted(field, item) {
      const formatter = field.formatter;
      const value = this.value(field, item);
      return formatter ? formatter(value, item) : value;
    },
    switchSort(columnName) {
      this.$emit('sortSwitched', columnName);
    },
    onRowClick(item, index) {
      this.$emit('rowClicked', item, index);
    },
    onSelect(item, items, itemIndex) {
      // Toggle the selected state of the item
      item.selected = !item.selected;

      // Check if selectedItems array is available
      if (this.selectedItems) {
        const foundIndex = this.selectedItems.findIndex(selectedItem => this.equal(selectedItem, item));

        if (item.selected) {
          if (foundIndex === -1) {
            // If item is selected and not in the selectedItems, add it with 'new' flag
            this.selectedItems.push(Object.assign({ new: true }, item));
          } else {
            // If item is selected and already in selectedItems, ensure it's marked as selected
            this.selectedItems[foundIndex].selected = true;
          }
        } else if (foundIndex >= 0) {
          // If item is deselected and in selectedItems, mark it as not selected
          this.selectedItems[foundIndex].selected = false;
        }
      }

      // Emit the 'selected' event
      this.$emit('selected', item, items, itemIndex);
    },
    equal(itemA, itemB) {
      const identifiers = this.fields.filter(field => field.identifier);
      if (identifiers.length === 0) return false;

      for (const id of identifiers) {
        if (this.value(id, itemA) !== this.value(id, itemB)) return false;
      }
      return true;
    },
    selected(item) {
      if (item.selected) {
        return true;
      } else if (this.selectedItems) {
        const identicalItem = this.selectedItems.find(selectedItem => this.equal(selectedItem, item));
        if (identicalItem) {
          item.selected = identicalItem.selected
        }
        return item.selected;
      }
    },
    snakeToCamel(str) {
      return str.replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('_', ''));
    },
  }
}
</script>

<style scoped>
/* table container and table */
.table-container {
  padding: 0 0.666rem 0.666rem 0.666rem;
  border-radius: 10px;
  background-color: #F6F6FA;
  overflow: auto;
  white-space: nowrap;
}

.table-container table.table {
  width: auto;
  min-width: 100%;
  border-collapse: separate; /* Don't collapse */
  border-spacing: 0;
}

table.table {
  table-layout: fixed;
}

table.table {
  margin-bottom: 0;
}

/* header */
table.table > thead {
  position: sticky;
  top: 0;
  z-index: 50;
}

table.table > thead > tr > th {
  height: 60px;
  padding: 0 .5vh;
  background-color: #F6F6FA;
  border: none;
  text-align: center;
  font-family: NanumGothic, serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  vertical-align: middle;
  white-space: nowrap;
  user-select: none; /* 드래그 불가능 */
}

/* body */
table.table > tbody {
  overflow-y: auto;
}

table.table tr {
  color: #2E2E38;
  background-color: #FFF;
}

/* 문경태 팀장님의 요청에 의해 hover 대신 스트라이프 사용 */
/*
table.table tr:hover {
  background-color: #FAFAFA;
}
 */
table.striped.table tr:nth-child(2n) {
  background-color: #FAFAFA;
}

table.table td {
  min-height: 80px;
  padding: 1rem 1rem;
  border-top: 1px solid #E7E7EE;
  font-family: NanumGothic, serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  vertical-align: middle;
}

table.table.table-sm td {
  padding: .6rem .8rem;
}

table.table.table-sm > thead > tr > th {
  height: 30px;
  padding: 10px 10px;
  border-top: none;
  border-bottom: none;
}

table.table.table-xs td {
  font-size: 12px;
  padding: .4rem .6rem;
}

table.table.table-xs > thead > tr > th {
  height: 25px;
  padding: 7px 7px;
  font-size: 12px;
  border-top: none;
  border-bottom: none;
}

table.table.table > thead > tr:first-child > th {
  border-bottom: solid 1px #D0D2D3;
}

table.table.table > thead > tr:first-child:only-child > th {
  border-right: none;
  border-left: none;
  border-bottom: none;
}

table.table.table > thead > tr > th:first-child {
  border-left: none;
  border-right: solid 1px #D0D2D3;
}

table.table.table > thead > tr > th:not(:first-child, :last-child) {
  border-right: solid 1px #D0D2D3;
}
</style>